import { useCallback, useEffect, useState } from 'react';

/**
 * useTheme — Design System v2 [UI-F0].
 *
 * Persiste a preferência em `localStorage('tcl-theme')` e reflete no
 * `<html data-theme>`, que é o seletor usado pelos tokens v2 e pelo
 * `darkMode` configurado no tailwind.config.js.
 *
 * Boot: ao montar o primeiro consumidor, lê localStorage e aplica.
 * Subsequentemente, qualquer chamada a `toggleTheme` alterna e persiste.
 * NÃO escutamos `prefers-color-scheme` — preferência do usuário sempre
 * vence; estratégia consciente vs. defaultar pelo SO.
 */

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'tcl-theme';

function ler(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    // localStorage indisponível (SSR/embed/cookies bloqueados) — cai no default.
  }
  return 'light';
}

function aplicar(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

interface UseThemeResult {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}

export function useTheme(): UseThemeResult {
  const [theme, setThemeState] = useState<Theme>(() => ler());

  useEffect(() => {
    aplicar(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggleTheme = useCallback(
    () => setThemeState((prev) => (prev === 'light' ? 'dark' : 'light')),
    [],
  );

  return { theme, toggleTheme, setTheme };
}
